import { computed } from 'vue'
import { useUserStore } from '@/stores/userStore'

export function usePermissions() {
  const userStore = useUserStore()

  // Permissões do Profile
  const canListUsers = computed(() => userStore.permissions.includes('Profile.list_users'))
  const canEditUser = computed(() => userStore.permissions.includes('Profile.edit_user'))
  const canChangeUserPassword = computed(() => userStore.permissions.includes('Profile.change_user_password'))
  const canDeleteUser = computed(() => userStore.permissions.includes('Profile.delete_user'))
  const canListGroups = computed(() => userStore.permissions.includes('Profile.list_groups'))
  const canAssignUserToGroup = computed(() => userStore.permissions.includes('Profile.assign_user_to_group'))

  // Permissões do Anamnesis
  const canCreateAnamnese = computed(() => userStore.permissions.includes('Anamnesis.create_anamnese'))
  const canEditAnamnese = computed(() => userStore.permissions.includes('Anamnesis.edit_anamnese'))
  const canRemoveAnamnese = computed(() => userStore.permissions.includes('Anamnesis.remove_anamnese'))
  const canListAnamneses = computed(() => userStore.permissions.includes('Anamnesis.list_anamneses'))
  const canUploadImageAnamnese = computed(() => userStore.permissions.includes('Anamnesis.upload_image_anamnese'))

  // Permissões do Monitor
  const canCreateMonitorUser = computed(() => userStore.permissions.includes('Monitor.create_monitor_user'))
  const canEditMonitorUser = computed(() => userStore.permissions.includes('Monitor.edit_monitor_user'))
  const canDeleteMonitorUser = computed(() => userStore.permissions.includes('Monitor.delete_monitor_user'))
  const canListMonitorUsers = computed(() => userStore.permissions.includes('Monitor.list_monitor_users'))
  const canViewCallPanel = computed(() => userStore.permissions.includes('Monitor.view_call_panel'))
  const canCallPatientFromPanel = computed(() => userStore.permissions.includes('Monitor.call_patient_from_panel'))

  // Permissões do MaskLaudos
  const canCreateMascaraLaudo = computed(() => userStore.permissions.includes('MaskLaudos.create_mascara_laudo'))
  const canEditMascaraLaudo = computed(() => userStore.permissions.includes('MaskLaudos.edit_mascara_laudo'))
  const canDeleteMascaraLaudo = computed(() => userStore.permissions.includes('MaskLaudos.delete_mascara_laudo'))
  const canListMascarasLaudo = computed(() => userStore.permissions.includes('MaskLaudos.list_mascaras_laudo'))
  const canManageSharedMascaras = computed(() => userStore.permissions.includes('MaskLaudos.manage_shared_mascaras'))

  // Permissões do Insurances
  const canCreateInsurance = computed(() => userStore.permissions.includes('Insurances.create_insurance'))
  const canEditInsurance = computed(() => userStore.permissions.includes('Insurances.edit_insurance'))
  const canRemoveInsurance = computed(() => userStore.permissions.includes('Insurances.remove_insurance'))
  const canListInsurances = computed(() => userStore.permissions.includes('Insurances.list_insurances'))

  // Permissões do Doctors
  const canCreateDoctor = computed(() => userStore.permissions.includes('Doctors.create_doctor'))
  const canEditDoctor = computed(() => userStore.permissions.includes('Doctors.edit_doctor'))
  const canRemoveDoctor = computed(() => userStore.permissions.includes('Doctors.remove_doctor'))
  const canListDoctors = computed(() => userStore.permissions.includes('Doctors.list_doctors'))

  // Permissões do Audit
  const canViewAuditLog = computed(() => userStore.permissions.includes('Audit.view_audit_log'))
  const canExportAuditLog = computed(() => userStore.permissions.includes('Audit.export_audit_log'))

  // Permissões do Patients
  const canCreatePatient = computed(() => userStore.permissions.includes('patients.create_patient'))
  const canEditPatient = computed(() => userStore.permissions.includes('patients.edit_patient'))
  const canRemovePatient = computed(() => userStore.permissions.includes('patients.remove_patient'))
  const canListPatients = computed(() => userStore.permissions.includes('patients.list_patients'))
  const canMergePatient = computed(() => userStore.permissions.includes('patients.merge_patient'))

  // Permissões do Notifications
  const canSendNotification = computed(() => userStore.permissions.includes('Notifications.send_notification'))
  const canManageNotificationSettings = computed(() => userStore.permissions.includes('Notifications.manage_notification_settings'))

  // Permissões do Schedules
  const canAddAppointment = computed(() => userStore.permissions.includes('Schedules.add_appointment'))
  const canEditAppointment = computed(() => userStore.permissions.includes('Schedules.change_appointment'))
  const canDeleteAppointment = computed(() => userStore.permissions.includes('Schedules.delete_appointment'))
  const canViewAppointment = computed(() => userStore.permissions.includes('Schedules.view_appointment'))

  // Permissões do MedicalReports
  const canCreateMedicalReport = computed(() => userStore.permissions.includes('MedicalReports.add_medicalreport'))
  const canEditMedicalReport = computed(() => userStore.permissions.includes('MedicalReports.change_medicalreport'))
  const canDeleteMedicalReport = computed(() => userStore.permissions.includes('MedicalReports.delete_medicalreport'))
  const canViewMedicalReport = computed(() => userStore.permissions.includes('MedicalReports.view_medicalreport'))

  // Permissões do Services
  const canCreateService = computed(() => userStore.permissions.includes('Services.add_servico'))
  const canEditService = computed(() => userStore.permissions.includes('Services.change_servico'))
  const canDeleteService = computed(() => userStore.permissions.includes('Services.delete_servico'))
  const canViewService = computed(() => userStore.permissions.includes('Services.view_servico'))

  // Permissões do RoomMedical
  const canCreateRoom = computed(() => userStore.permissions.includes('RoomMedical.add_room'))
  const canEditRoom = computed(() => userStore.permissions.includes('RoomMedical.change_room'))
  const canDeleteRoom = computed(() => userStore.permissions.includes('RoomMedical.delete_room'))
  const canViewRoom = computed(() => userStore.permissions.includes('RoomMedical.view_room'))

  // Permissões do Pacs
  const canCreateStudy = computed(() => userStore.permissions.includes('Pacs.add_study'))
  const canEditStudy = computed(() => userStore.permissions.includes('Pacs.change_study'))
  const canDeleteStudy = computed(() => userStore.permissions.includes('Pacs.delete_study'))
  const canViewStudy = computed(() => userStore.permissions.includes('Pacs.view_study'))

  // Permissões do ClinicSettings
  const canEditClinicSettings = computed(() => userStore.permissions.includes('ClinicSettings.change_clinic'))
  const canViewClinicSettings = computed(() => userStore.permissions.includes('ClinicSettings.view_clinic'))

  // Função genérica para verificar permissão
  const hasPermission = (permission) => {
    return userStore.permissions.includes(permission)
  }

  // Função para verificar se tem qualquer uma das permissões
  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => userStore.permissions.includes(permission))
  }

  // Função para verificar se tem todas as permissões
  const hasAllPermissions = (permissions) => {
    return permissions.every(permission => userStore.permissions.includes(permission))
  }

  // Verificar se é superusuário
  const isSuperuser = computed(() => userStore.grupos.includes('superadmin'))

  return {
    // Profile permissions
    canListUsers,
    canEditUser,
    canChangeUserPassword,
    canDeleteUser,
    canListGroups,
    canAssignUserToGroup,

    // Anamnesis permissions
    canCreateAnamnese,
    canEditAnamnese,
    canRemoveAnamnese,
    canListAnamneses,
    canUploadImageAnamnese,

    // Monitor permissions
    canCreateMonitorUser,
    canEditMonitorUser,
    canDeleteMonitorUser,
    canListMonitorUsers,
    canViewCallPanel,
    canCallPatientFromPanel,

    // MaskLaudos permissions
    canCreateMascaraLaudo,
    canEditMascaraLaudo,
    canDeleteMascaraLaudo,
    canListMascarasLaudo,
    canManageSharedMascaras,

    // Insurances permissions
    canCreateInsurance,
    canEditInsurance,
    canRemoveInsurance,
    canListInsurances,

    // Doctors permissions
    canCreateDoctor,
    canEditDoctor,
    canRemoveDoctor,
    canListDoctors,

    // Audit permissions
    canViewAuditLog,
    canExportAuditLog,

    // Patients permissions
    canCreatePatient,
    canEditPatient,
    canRemovePatient,
    canListPatients,
    canMergePatient,

    // Notifications permissions
    canSendNotification,
    canManageNotificationSettings,

    // Schedules permissions
    canAddAppointment,
    canEditAppointment,
    canDeleteAppointment,
    canViewAppointment,

    // MedicalReports permissions
    canCreateMedicalReport,
    canEditMedicalReport,
    canDeleteMedicalReport,
    canViewMedicalReport,

    // Services permissions
    canCreateService,
    canEditService,
    canDeleteService,
    canViewService,

    // RoomMedical permissions
    canCreateRoom,
    canEditRoom,
    canDeleteRoom,
    canViewRoom,

    // Pacs permissions
    canCreateStudy,
    canEditStudy,
    canDeleteStudy,
    canViewStudy,

    // ClinicSettings permissions
    canEditClinicSettings,
    canViewClinicSettings,

    // Utility functions
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperuser
  }
}